import { useState, useEffect, useCallback } from 'react'
import { format, startOfWeek, endOfWeek, addDays, startOfDay } from 'date-fns'
import { toast } from 'sonner'
import { useAuth } from '@/context/auth-context'
import { useSettings } from '@/context/settings-context'

export interface RawGroupedItem {
  calendar_id: string
  life_domain_id: string | null
  life_domain_title: string
  life_domain_color: string
  life_domain_sort_order: number
  label_id: string | null
  label_name: string
  label_color: string
  priority: string
  events_type: string
  intentional_living: boolean
  time_spent: number
}

export interface CalendarMeta {
  id: string
  name: string
}

interface DailyItem {
  day: string
  day_label: string
  period_duration: number
  intentional_living: number
  other: number
  unscheduled: number
  intentional_living_percentage: number
}

interface AggregateStats {
  period_duration: number
  intentional_living: number
  other: number
  unscheduled: number
  intentional_living_percentage: number
  wow_intentional_living?: number | null
}

export interface EventListItem {
  id: string
  integrations_calendar_id: string
  event_id: string
  event_title: string | null
  event_start_time: string
  event_end_time: string
  event_color: string | null
  event_color_id: string | null
  event_html_link: string | null
  event_is_blocking: boolean
}

export interface InsightsData {
  raw_data: RawGroupedItem[]
  aggregate: AggregateStats
  daily: DailyItem[]
  calendars: CalendarMeta[]
  events?: EventListItem[]
  planned_data?: {
    hasPlan: boolean
    data?: {
      aggregate: {
        intentional_living_actual: number
        intentional_living_planned: number
        intentional_living_completion: number
        intentional_living_commitment: number
        wow_execution: number | null
      }
      daily?: Array<{
        day: string
        actual: number
        planned: number
        completion: number
      }>
      life_domain?: Array<{
        label: string
        life_domain: string
        color: string
        is_intentional_living: boolean
        sort_order?: number
        actual: number
        planned: number
        completion: number
      }>
      label?: Array<{
        label: string
        label_id: string
        color: string
        is_intentional_living: boolean
        actual: number
        planned: number
        completion: number
      }>
      priority?: Array<{
        label: string
        priority: string
        color: string
        is_intentional_living: boolean
        actual: number
        planned: number
        completion: number
      }>
      type?: Array<{
        label: string
        type: string
        color: string
        is_intentional_living: boolean
        actual: number
        planned: number
        completion: number
      }>
    }
  }
  is_existing_plan?: boolean
  updated_at?: string
}

interface InsightsResponse {
  success: boolean
  data: InsightsData
  message?: string
}

export function useInsightsV2(weekStart: Date, mode: 'actual' | 'planned' = 'actual', listEvents: boolean = false) {
  const { session } = useAuth()
  const { timezone, weekStartsOn } = useSettings()
  const [data, setData] = useState<InsightsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchInsights = useCallback(async (startDate: Date, _refresh: boolean = false, _channelId?: string) => {
    if (!session?.access_token) {
      setError('Authentication required')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setData(null)

    try {
      const weekStartsOnNumber = weekStartsOn === 'monday' ? 1 : 0
      
      const weekEnd = endOfWeek(startDate, { weekStartsOn: weekStartsOnNumber })
      
      const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: weekStartsOnNumber })
      const isFutureWeek = startDate.getTime() > currentWeekStart.getTime()

      // Block future weeks only for actual mode (insights). Planning page needs to support future weeks.
      if (mode === 'actual' && isFutureWeek) {
        setData(null)
        toast.info('No data available yet')
        setLoading(false)
        return
      }

      const startDateStr = format(startDate, 'yyyy-MM-dd')

      const exclusiveWeekEnd = startOfDay(addDays(weekEnd, 1))
      let effectiveEnd: Date
      if (mode === 'actual') {
        const now = new Date()
        effectiveEnd = now < exclusiveWeekEnd ? now : exclusiveWeekEnd
      } else {
        effectiveEnd = exclusiveWeekEnd
      }

      const requestBody = {
        startDate: startDateStr,
        endDateTime: effectiveEnd.toISOString(),
        timezone: timezone,
        mode,
        listEvents,
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analytics-insights`

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      }).catch(err => {
        throw new Error(`Network error: ${err.message}. This might be a CORS issue or the endpoint may not accept POST requests.`)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`)
      }

      const result: InsightsResponse = await response.json()
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch insights data')
      }

      setData(result.data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch insights data'
      setError(errorMessage)
      console.error('Error fetching insights:', err)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [session?.access_token, timezone, weekStartsOn, mode, listEvents])

  useEffect(() => {
    if (session?.access_token) {
      fetchInsights(weekStart)
    }
  }, [weekStart, session?.access_token, timezone, weekStartsOn, mode, fetchInsights])

  return {
    data,
    loading,
    error,
    refetch: () => fetchInsights(weekStart),
    refresh: async (channelId?: string) => {
      try {
        await fetchInsights(weekStart, true, channelId)
        return { success: true }
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to refresh insights' }
      }
    }
  }
}
